/**
 * Load Demo Modal (Stage 10 — Demo Datasets).
 *
 * Reusable dataset picker. Lists every dataset in `DEMO_DATASETS` by name +
 * description, and loads the selected snapshot into the store atomically via
 * `importState`. Used by the header nav link, the dashboard loader, and the
 * setup-wizard escape hatch.
 */

import React from 'react';
import { useAppStore } from '@/store';
import { Modal, Button, useToast } from '../shared';
import { DEMO_DATASETS } from '@/data/demoDatasets';

export interface LoadDemoModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a dataset is loaded successfully. */
  onLoaded?: () => void;
}

export function LoadDemoModal({ isOpen, onClose, onLoaded }: LoadDemoModalProps): React.ReactElement {
  const { addToast } = useToast();
  const importState = useAppStore((s) => s.importState);
  const updateSettings = useAppStore((s) => s.updateSettings);

  const handleSelect = (datasetId: string) => {
    const dataset = DEMO_DATASETS.find((d) => d.id === datasetId);
    if (!dataset) return;

    importState(dataset.state);
    // Ensure the Load Demo nav link remains visible after loading.
    updateSettings({ demoLoaded: true });
    addToast({
      severity: 'success',
      message: `Loaded "${dataset.name}". Explore the app with this sample data.`,
    });
    onLoaded?.();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Load Demo Dataset" size="md">
      <div className="load-demo">
        <p className="load-demo__hint">
          Pick a pre-built dataset to explore Absence Budget with realistic attendance data.
          Loading replaces your current data.
        </p>
        <ul className="load-demo__list" role="list">
          {DEMO_DATASETS.map((dataset) => (
            <li key={dataset.id} className="load-demo__item">
              <div className="load-demo__item-info">
                <strong className="load-demo__item-name">{dataset.name}</strong>
                <span className="load-demo__item-desc">{dataset.description}</span>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleSelect(dataset.id)}
                aria-label={`Load ${dataset.name} dataset`}
              >
                Load
              </Button>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
